import { useEffect, useCallback, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useDocumentTitle } from "../../hooks/useDocumentTitle";
import { motion } from "framer-motion";
import { CircleNotch, ArrowClockwise, WarningCircle, House } from "@phosphor-icons/react";
import { AppLayout } from "../../components/layout/AppLayout";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import { LeverEditor } from "../../components/editor/LeverEditor";
import { DocxEditor } from "../../components/editor/DocxEditor";
import { SelectionToolbar } from "../../components/editor/SelectionToolbar";
import { EditorToolbar } from "../../components/editor/EditorToolbar";
import { DocxToolbar } from "../../components/editor/DocxToolbar";
import { StatusBar } from "../../components/editor/StatusBar";
import { KeyboardShortcutsDialog } from "../../components/editor/KeyboardShortcutsDialog";
import { ShareDialog } from "../../components/collaboration/ShareDialog";
import { PreviewPanel } from "../../components/preview/PreviewPanel";
import { FileTree } from "./FileTree";
import { ErrorList } from "./ErrorList";
import { ChatPanel } from "../ai/ChatPanel";
import { AIInlineSuggestion } from "../ai/AIInlineSuggestion";
import { EquationPreview } from "../ai/EquationPreview";
import { DiffOverlay } from "../../components/editor/DiffOverlay";
import { InlineAssistant } from "../ai/InlineAssistant";
import { VersionPanel } from "../version-control/VersionPanel";
import { CommentThread } from "../../components/collaboration/CommentThread";
import { CommentPopover } from "../../components/collaboration/CommentPopover";
import { OutlineTree } from "../../components/navigation/OutlineTree";
import { BreadcrumbBar } from "../../components/navigation/BreadcrumbBar";
import { useDocumentStore } from "../../stores/document";
import { useAIStore } from "../../stores/ai";
import { useAgentChatStore } from "../../stores/agentChat";
import { useEditorStore } from "../../stores/editor";
import { useUIStore } from "../../stores/ui";
import { useCollaborationStore } from "../../stores/collaboration";
import { useCollaboration } from "../collaboration/useCollaboration";
import { PresenceProvider } from "../../components/presence/PresenceProvider";
import { PresenceRipple } from "../../components/presence/PresenceRipple";
import { EquationPlayground } from "../../components/equation/EquationPlayground";
import { ContextPanel } from "../../components/context/ContextPanel";
import { VitalsDashboard } from "../../components/vitals/VitalsDashboard";
import { CitationPanel } from "../../components/citations/CitationPanel";
import { useVitalsStore } from "../../stores/vitals";
import { useKeyboardShortcuts } from "../../hooks/useKeyboardShortcuts";
import { useInlineAssistantStore } from "../../stores/inlineAssistant";
import { useAutoSave } from "../../hooks/useAutoSave";
import { useOfflineSync } from "../../hooks/useOfflineSync";
import { OfflineBanner } from "../../components/ui/OfflineBanner";
import { useToast } from "../../components/ui/Toast";
import { api } from "../../lib/api";
import type { EditEvent } from "../../types/ai";

const spring = { type: "spring" as const, stiffness: 500, damping: 35, mass: 0.6 };

function WorkspaceLoading() {
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-[var(--bg-primary)]">
      <div className="flex flex-col items-center gap-3">
        <CircleNotch size={20} weight="bold" className="animate-spin text-[var(--text-muted)]" />
        <p className="text-[11px] text-[var(--text-muted)]">Loading workspace...</p>
      </div>
    </div>
  );
}

function WorkspaceError({ message, onRetry, onHome }: { message: string; onRetry: () => void; onHome: () => void }) {
  return (
    <div className="flex h-screen w-screen items-center justify-center bg-[var(--bg-primary)]">
      <div className="flex flex-col items-center gap-4 max-w-sm text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--color-soft-red)]/10">
          <WarningCircle size={24} weight="bold" className="text-[var(--color-soft-red)]" />
        </div>
        <div>
          <p className="text-[13px] font-medium text-[var(--text-primary)]">
            Could not load workspace
          </p>
          <p className="text-[11px] text-[var(--text-muted)] mt-1">{message}</p>
        </div>
        <div className="flex items-center gap-2">
          <motion.button
            onClick={onRetry}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.96 }}
            transition={spring}
            className="flex items-center gap-1.5 rounded-[var(--radius-sm)] px-3 py-1.5 text-[11px] font-medium bg-[var(--text-primary)] text-[var(--text-inverse)] hover:opacity-90 transition-opacity duration-100"
          >
            <ArrowClockwise size={12} weight="bold" />
            Retry
          </motion.button>
          <motion.button
            onClick={onHome}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.96 }}
            transition={spring}
            className="flex items-center gap-1.5 rounded-[var(--radius-sm)] px-3 py-1.5 text-[11px] font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] border border-[var(--border-hairline)] hover:bg-[var(--bg-surface-hover)] transition-colors duration-100"
          >
            <House size={12} weight="bold" />
            Dashboard
          </motion.button>
        </div>
      </div>
    </div>
  );
}

function WorkspaceInner() {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const {
    currentProject,
    currentDocument,
    documents,
    errors,
    unsavedChanges,
    pendingEdit,
    fetchDocuments,
    openDocument,
    updateContent,
    saveDocument,
    compile,
    proposePendingEdit,
    acceptPendingEdit,
    rejectPendingEdit,
  } = useDocumentStore();

  useDocumentTitle(currentProject?.name ?? "Workspace");
  useAutoSave();
  const { isOnline, isSyncing, pendingCount } = useOfflineSync();

  const editTextStream = useAIStore((s) => s.editTextStream);
  const explainText = useAIStore((s) => s.explainText);
  const editorView = useEditorStore((s) => s.editorView);
  const cursorLine = useEditorStore((s) => s.cursorLine);
  const selection = useEditorStore((s) => s.selection);
  const sendAgentMessage = useAgentChatStore((s) => s.sendMessage);
  const openAgentChat = useAgentChatStore((s) => s.setOpen);
  const refreshAgentConversations = useAgentChatStore((s) => s.refreshConversationsForDocument);
  const agentActiveDocId = useAgentChatStore((s) => s.activeDocumentId);
  const isDocx = currentDocument?.doc_type === "docx";
  const compileResult = useDocumentStore((s) => s.compileResult);
  const openBottomPanelTab = useUIStore((s) => s.openBottomPanelTab);
  const toggleInlineAssistant = useInlineAssistantStore((s) => s.toggle);
  const inlineAssistantOpen = useInlineAssistantStore((s) => s.isOpen);

  const { awareness, isConnected } = useCollaboration({
    documentId: currentDocument?.id ?? null,
  });
  const comments = useCollaborationStore((s) => s.comments);
  const fetchComments = useCollaborationStore((s) => s.fetchComments);
  const addComment = useCollaborationStore((s) => s.addComment);
  const resolveComment = useCollaborationStore((s) => s.resolveComment);
  const deleteComment = useCollaborationStore((s) => s.deleteComment);
  const { success: toastSuccess, ai: toastAI, error: toastError, info: toastInfo } = useToast();
  const isCompiling = useDocumentStore((s) => s.isCompiling);
  const saveFailed = useDocumentStore((s) => s.saveFailed);
  const prevCompilingRef = useRef(false);

  useEffect(() => {
    if (prevCompilingRef.current && !isCompiling) {
      const result = useDocumentStore.getState().compileResult;
      if (result?.success) {
        toastSuccess("Compiled", `${result.compile_time_ms}ms`);
      } else if (result && !result.success) {
        toastError("Compilation failed", result.errors?.[0]?.message?.slice(0, 80) || "Check logs for details");
      }
    }
    prevCompilingRef.current = isCompiling;
  }, [isCompiling, toastSuccess, toastError]);

  useEffect(() => {
    return useDocumentStore.getState().onSaveEvent((event) => {
      if (event === "save_failed") {
        toastError("Save failed", "Your changes are not saved. Check your connection.");
      } else if (event === "save_recovered") {
        toastSuccess("Saved", "Connection restored, document saved.");
      }
    });
  }, [toastError, toastSuccess]);

  const prevConnectedRef = useRef(false);
  const initialConnectionRef = useRef(true);
  const disconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const didShowDisconnectRef = useRef(false);
  useEffect(() => {
    if (initialConnectionRef.current) {
      if (isConnected) initialConnectionRef.current = false;
      prevConnectedRef.current = isConnected;
      return;
    }

    if (prevConnectedRef.current && !isConnected && currentDocument) {
      // Debounce: only show disconnect toast if still disconnected after 2s
      disconnectTimerRef.current = setTimeout(() => {
        toastError("Disconnected", "Real-time sync lost. Your edits are saved locally.");
        didShowDisconnectRef.current = true;
      }, 2000);
    } else if (!prevConnectedRef.current && isConnected && currentDocument) {
      if (disconnectTimerRef.current) {
        clearTimeout(disconnectTimerRef.current);
        disconnectTimerRef.current = null;
      }
      if (didShowDisconnectRef.current) {
        toastInfo("Reconnected", "Real-time sync restored.");
        didShowDisconnectRef.current = false;
      }
    }
    prevConnectedRef.current = isConnected;

    return () => {
      if (disconnectTimerRef.current) {
        clearTimeout(disconnectTimerRef.current);
        disconnectTimerRef.current = null;
      }
    };
  }, [isConnected, currentDocument, toastError, toastInfo]);

  const [loadState, setLoadState] = useState<"loading" | "ready" | "error">("loading");
  const [loadError, setLoadError] = useState("");
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shortcutsDialogOpen, setShortcutsDialogOpen] = useState(false);

  const loadWorkspace = useCallback(async () => {
    if (!projectId) return;
    setLoadState("loading");
    setLoadError("");
    try {
      await fetchDocuments(projectId);
      setLoadState("ready");
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : "Failed to connect to server");
      setLoadState("error");
    }
  }, [projectId, fetchDocuments]);

  useEffect(() => { loadWorkspace(); }, [loadWorkspace]);

  const vitalsAnalyze = useVitalsStore((s) => s.analyze);
  useEffect(() => {
    if (currentDocument?.content) {
      vitalsAnalyze(currentDocument.content, currentDocument.doc_type || "latex");
    }
  }, [currentDocument?.content, currentDocument?.doc_type, vitalsAnalyze]);

  useEffect(() => {
    if (loadState === "ready" && documents.length > 0 && !currentDocument) {
      openDocument(documents[0].id);
    }
  }, [loadState, documents, currentDocument, openDocument]);

  useEffect(() => {
    if (currentDocument?.id) fetchComments(currentDocument.id);
  }, [currentDocument?.id, fetchComments]);

  // Hydrate agent chat sidebar when the active document changes
  useEffect(() => {
    if (currentDocument?.id && currentDocument.id !== agentActiveDocId) {
      refreshAgentConversations(currentDocument.id, projectId ?? null);
    }
  }, [currentDocument?.id, agentActiveDocId, projectId, refreshAgentConversations]);

  const handleContentChange = useCallback(
    (content: string) => updateContent(content),
    [updateContent],
  );

  const handleCompile = useCallback(() => {
    saveDocument()
      .then(() => compile())
      .catch((err) => {
        if (err instanceof Error) {
          toastError("Compile failed", err.message.slice(0, 80));
        }
      });
  }, [saveDocument, compile, toastError]);

  useKeyboardShortcuts({
    save: handleCompile,
    compile: handleCompile,
  });

  useEffect(() => {
    const handleInlineAssistantShortcut = (e: KeyboardEvent) => {
      const mod = navigator.platform.toUpperCase().includes("MAC")
        ? e.metaKey
        : e.ctrlKey;
      if (mod && e.key === "g") {
        e.preventDefault();
        e.stopPropagation();
        toggleInlineAssistant();
      }
    };
    window.addEventListener("keydown", handleInlineAssistantShortcut, {
      capture: true,
    });
    return () =>
      window.removeEventListener("keydown", handleInlineAssistantShortcut, {
        capture: true,
      });
  }, [toggleInlineAssistant]);

  const handleSelectionAction = useCallback(
    async (actionId: string, selectedText: string) => {
      if (!currentDocument) return;
      if (actionId === "comment") return;

      const textIndex = currentDocument.content.indexOf(selectedText);
      const contextStart = Math.max(0, textIndex - 200);
      const contextEnd = Math.min(currentDocument.content.length, textIndex + selectedText.length + 200);
      const context = currentDocument.content.substring(contextStart, contextEnd);

      if (actionId === "explain") {
        try {
          const resp = await explainText(selectedText, context);
          toastAI("Explanation", resp.result.slice(0, 120) + (resp.result.length > 120 ? "..." : ""));
        } catch {
          toastError("Explain failed", "Check your AI provider configuration.");
        }
        return;
      }

      const validActions = ["rewrite", "simplify", "expand", "fix", "solve", "prove", "cite", "outline", "hint", "proofread"] as const;
      type ValidAction = (typeof validActions)[number];
      const action = validActions.includes(actionId as ValidAction) ? (actionId as ValidAction) : "rewrite";
      try {
        await editTextStream(selectedText, action, context, currentDocument.doc_type);
        toastAI("Suggestion ready", `${action.charAt(0).toUpperCase() + action.slice(1)} complete`);
      } catch {
        toastError("AI action failed", "Check your AI provider configuration.");
      }
    },
    [currentDocument, editTextStream, explainText, toastAI, toastError],
  );

  const handleOpenHistory = useCallback(() => {
    openBottomPanelTab("versions");
  }, [openBottomPanelTab]);

  const handleDownloadSource = useCallback(() => {
    if (!currentDocument) return;
    const blob = new Blob([currentDocument.content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = currentDocument.filename || "document.tex";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [currentDocument]);

  const handleDownloadPdf = useCallback(() => {
    const pdfUrl = useDocumentStore.getState().compileResult?.pdf_url;
    if (!pdfUrl) return;
    const a = document.createElement("a");
    a.href = pdfUrl;
    a.download = "document.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, []);

  const handleDownloadDocx = useCallback(() => {
    if (!currentDocument) return;
    const a = document.createElement("a");
    a.href = `${api.getBaseUrl()}/api/v1/documents/${currentDocument.id}/export/docx`;
    a.download = currentDocument.filename?.replace(/\.\w+$/, ".docx") ?? "document.docx";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }, [currentDocument]);

  const handlePrint = useCallback(() => {
    if (isDocx) {
      window.print();
      return;
    }
    const pdfUrl = useDocumentStore.getState().compileResult?.pdf_url;
    if (!pdfUrl) return;
    const iframe = document.createElement("iframe");
    iframe.style.display = "none";
    iframe.src = pdfUrl;
    document.body.appendChild(iframe);
    iframe.onload = () => {
      iframe.contentWindow?.print();
      setTimeout(() => document.body.removeChild(iframe), 1000);
    };
  }, [isDocx]);

  const handleWordCount = useCallback(() => {
    openBottomPanelTab("summary");
  }, [openBottomPanelTab]);

  const scrollToLine = useCallback(
    (line: number) => {
      if (!editorView) return;
      const clampedLine = Math.max(1, Math.min(line, editorView.state.doc.lines));
      const lineInfo = editorView.state.doc.line(clampedLine);
      editorView.dispatch({ selection: { anchor: lineInfo.from }, scrollIntoView: true });
      editorView.focus();
    },
    [editorView],
  );

  const handlePreviewAIFix = useCallback(() => {
    const errorMessages = errors
      .filter((e) => e.severity === "error")
      .map((e) => `${e.message}${e.line ? ` (line ${e.line})` : ""}`)
      .join("; ");
    if (!errorMessages.trim()) return;
    openAgentChat(true);
    sendAgentMessage({
      message: `Fix these compilation errors in my document: ${errorMessages}`,
      documentId: currentDocument?.id,
      projectId: projectId,
      cursorLine,
      selectionStart: selection?.from,
      selectionEnd: selection?.to,
      selectedText: selection?.text,
      onEdit: (edit: EditEvent) => {
        const proposed = edit.full_new_content ?? edit.newContent ?? edit.content;
        if (proposed) proposePendingEdit(proposed, "AI suggested fix");
      },
    });
  }, [
    errors,
    openAgentChat,
    sendAgentMessage,
    currentDocument?.id,
    projectId,
    cursorLine,
    selection,
    proposePendingEdit,
  ]);

  const handleCommentFromPopover = useCallback(
    (content: string, lineStart: number, lineEnd: number) => {
      const now = new Date().toISOString();
      addComment({
        id: crypto.randomUUID(),
        document_id: currentDocument?.id || "",
        user_id: "local",
        content,
        line_start: lineStart,
        line_end: lineEnd,
        parent_id: null,
        resolved: false,
        created_at: now,
        updated_at: now,
      });
    },
    [addComment, currentDocument?.id],
  );

  const handleReply = useCallback(
    (content: string, parentId: string) => {
      const parent = comments.find((c) => c.id === parentId);
      const now = new Date().toISOString();
      addComment({
        id: crypto.randomUUID(),
        document_id: currentDocument?.id || "",
        user_id: "local",
        content,
        line_start: parent?.line_start ?? 1,
        line_end: parent?.line_end ?? 1,
        parent_id: parentId,
        resolved: false,
        created_at: now,
        updated_at: now,
      });
    },
    [addComment, currentDocument?.id, comments],
  );

  if (loadState === "loading") return <WorkspaceLoading />;
  if (loadState === "error") return <WorkspaceError message={loadError} onRetry={loadWorkspace} onHome={() => navigate("/")} />;
  if (loadState === "ready" && documents.length === 0 && !currentDocument) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-[var(--bg-primary)]">
        <div className="flex flex-col items-center gap-4 max-w-sm text-center">
          <div>
            <p className="text-[13px] font-medium text-[var(--text-primary)]">
              This project has no files yet
            </p>
            <p className="text-[11px] text-[var(--text-muted)] mt-1">Create a file to start writing.</p>
          </div>
          <motion.button
            onClick={async () => {
              if (!projectId) return;
              const doc = await useDocumentStore.getState().createDocument(projectId, "main.tex", "latex");
              openDocument(doc.id);
            }}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.96 }}
            transition={spring}
            className="flex items-center gap-1.5 rounded-[var(--radius-sm)] px-3 py-1.5 text-[11px] font-medium bg-[var(--text-primary)] text-[var(--text-inverse)] hover:opacity-90 transition-opacity duration-100"
          >
            Create first file
          </motion.button>
          <motion.button
            onClick={() => navigate("/")}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.96 }}
            transition={spring}
            className="flex items-center gap-1.5 rounded-[var(--radius-sm)] px-3 py-1.5 text-[11px] font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)] border border-[var(--border-hairline)] hover:bg-[var(--bg-surface-hover)] transition-colors duration-100"
          >
            <House size={12} weight="bold" />
            Dashboard
          </motion.button>
        </div>
      </div>
    );
  }

  return (
    <>
      <AppLayout
        documentTitle={currentDocument?.filename || "Untitled"}
        documentType={currentDocument?.doc_type || "latex"}
        isSaved={!unsavedChanges}
        errorCount={errors.length}
        onOpenHistory={handleOpenHistory}
        onShare={() => setShareDialogOpen(true)}
        onDownloadSource={handleDownloadSource}
        onDownloadPdf={isDocx ? undefined : handleDownloadPdf}
        onDownloadDocx={isDocx ? handleDownloadDocx : undefined}
        onPrint={handlePrint}
        onOpenShortcuts={() => setShortcutsDialogOpen(true)}
        onWordCount={handleWordCount}
        editorSlot={
          <PresenceProvider awareness={awareness} userName="You" userColor="#5B5FFF">
            <div className="relative h-full flex flex-col bg-[var(--bg-surface)]">
              <OfflineBanner isOnline={isOnline} isSyncing={isSyncing} pendingCount={pendingCount} />
              {saveFailed && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-[var(--color-soft-red)]/10 border-b border-[var(--color-soft-red)]/20 text-[11px] text-[var(--color-soft-red)]">
                  <WarningCircle size={14} weight="bold" />
                  <span>Unable to save. Check your connection.</span>
                  <button
                    onClick={() => saveDocument()}
                    className="ml-auto underline underline-offset-2 hover:opacity-80 transition-opacity"
                  >
                    Retry
                  </button>
                </div>
              )}
              {!isConnected && currentDocument && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/10 border-b border-amber-500/20 text-[11px] text-amber-600 dark:text-amber-400">
                  <WarningCircle size={14} weight="bold" />
                  <span>Real-time sync disconnected. Edits are local only.</span>
                </div>
              )}
              {!isDocx && (
                <BreadcrumbBar
                  content={currentDocument?.content || ""}
                  docType={currentDocument?.doc_type || "latex"}
                  currentLine={cursorLine}
                  onNavigate={scrollToLine}
                />
              )}
              {isDocx ? <DocxToolbar /> : <EditorToolbar />}
              <div className="relative flex-1 min-h-0">
                {isDocx ? (
                  <DocxEditor
                    value={currentDocument?.content || ""}
                    onChange={handleContentChange}
                  />
                ) : (
                  <LeverEditor
                    value={currentDocument?.content || ""}
                    onChange={handleContentChange}
                    language={currentDocument?.doc_type || "latex"}
                  />
                )}
                <SelectionToolbar onAction={handleSelectionAction} />
                {!isDocx && <CommentPopover onSubmit={handleCommentFromPopover} />}
                {!isDocx && <EquationPreview />}
                <PresenceRipple />
                {!isDocx && !inlineAssistantOpen && (
                  <div className="absolute bottom-3 left-3 right-3 z-30">
                    <AIInlineSuggestion />
                  </div>
                )}
                <div className="absolute bottom-3 left-3 right-3 z-[35] flex justify-center pointer-events-none">
                  <div className="pointer-events-auto w-full">
                    <InlineAssistant
                      projectId={projectId}
                      files={documents.map((d) => ({ id: d.id, filename: d.filename, doc_type: d.doc_type }))}
                      activeFileId={currentDocument?.id}
                    />
                  </div>
                </div>
                {pendingEdit && currentDocument && (
                  <DiffOverlay
                    originalContent={currentDocument.content}
                    proposedContent={pendingEdit.proposedContent}
                    description={pendingEdit.description}
                    onAccept={acceptPendingEdit}
                    onReject={rejectPendingEdit}
                  />
                )}
              </div>
              <StatusBar
                content={currentDocument?.content || ""}
                docType={currentDocument?.doc_type || "latex"}
              />
            </div>
          </PresenceProvider>
        }
        hidePreview={isDocx}
        previewSlot={<PreviewPanel onAIFix={!isDocx ? handlePreviewAIFix : undefined} />}
        filesContent={
          <FileTree
            documents={documents}
            activeDocumentId={currentDocument?.id || null}
            onSelect={(id) => openDocument(id)}
            projectId={projectId || ""}
          />
        }
        outlineContent={
          <OutlineTree
            content={currentDocument?.content || ""}
            docType={currentDocument?.doc_type || "latex"}
            activeLine={cursorLine}
            onNavigate={scrollToLine}
          />
        }
        commentsContent={
          <CommentThread
            comments={comments}
            documentId={currentDocument?.id}
            onAddComment={(content, lineStart, lineEnd, parentId) => {
              const now = new Date().toISOString();
              addComment({
                id: crypto.randomUUID(),
                document_id: currentDocument?.id || "",
                user_id: "local",
                content,
                line_start: lineStart,
                line_end: lineEnd,
                parent_id: parentId || null,
                resolved: false,
                created_at: now,
                updated_at: now,
              });
            }}
            onResolveComment={resolveComment}
            onDeleteComment={deleteComment}
            onReply={handleReply}
            onNavigateToLine={scrollToLine}
          />
        }
        chatContent={<ChatPanel />}
        citationsContent={<CitationPanel documentContent={currentDocument?.content} docType={currentDocument?.doc_type} />}
        contextContent={<ContextPanel />}
        errorsContent={<ErrorList errors={errors} onNavigate={scrollToLine} />}
        logsContent={
          compileResult?.log ? (
            <pre className="p-3 text-[11px] leading-relaxed font-mono text-[var(--text-secondary)] whitespace-pre-wrap max-h-[400px] overflow-auto">
              {compileResult.log}
            </pre>
          ) : (
            <div className="p-3 text-xs text-[var(--text-muted)]">
              No compilation logs yet. Compile your document to see output.
            </div>
          )
        }
        vitalsContent={<VitalsDashboard />}
        versionsContent={<VersionPanel />}
      />

      <ShareDialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        projectId={projectId}
        documentTitle={currentDocument?.filename}
      />

      <KeyboardShortcutsDialog
        open={shortcutsDialogOpen}
        onClose={() => setShortcutsDialogOpen(false)}
      />

      <EquationPlayground />
    </>
  );
}

export function WorkspacePage() {
  return (
    <ErrorBoundary fallbackMessage="The workspace encountered an unexpected error.">
      <WorkspaceInner />
    </ErrorBoundary>
  );
}
